/**
 * Cart Drawer - AJAX cart functionality
 */

class CartDrawer {
  constructor() {
    this.drawer = document.querySelector('[data-cart-drawer]');
    if (!this.drawer) return;

    this.overlay = this.drawer.querySelector('[data-cart-drawer-overlay]');
    this.content = this.drawer.querySelector('[data-cart-drawer-content]');
    this.panel = this.drawer.querySelector('.cart-drawer__panel');

    // Initialize focus trap
    this.focusTrap = null;
    if (window.FocusTrap && this.panel) {
      this.focusTrap = new window.FocusTrap(this.panel, {
        onEscape: () => this.close()
      });
    }

    this.bindEvents();
  }

  bindEvents() {
    // Open cart drawer
    document.querySelectorAll('[data-cart-drawer-open]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    // Close cart drawer
    document.querySelectorAll('[data-cart-drawer-close]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.close();
      });
    });

    // Close on overlay click
    this.overlay?.addEventListener('click', () => this.close());

    // Close on escape key (fallback if focus trap not loaded)
    if (!this.focusTrap) {
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) {
          this.close();
        }
      });
    }

    // Quantity change
    this.drawer.addEventListener('click', (e) => {
      const qtyBtn = e.target.closest('[data-change-qty]');
      if (qtyBtn) {
        this.handleQuantityChange(qtyBtn);
      }

      const removeBtn = e.target.closest('[data-remove-item]');
      if (removeBtn) {
        this.handleRemoveItem(removeBtn);
      }
    });

    // Intercept add to cart forms
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('form[action="/cart/add"]');
      if (form) {
        e.preventDefault();
        this.handleAddToCart(form);
      }
    });
  }

  open() {
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    // Activate focus trap after animation
    if (this.focusTrap) {
      setTimeout(() => this.focusTrap.activate(), 300);
    }
  }

  close() {
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    // Deactivate focus trap
    if (this.focusTrap) {
      this.focusTrap.deactivate();
    }
  }

  async handleAddToCart(form) {
    const formData = new FormData(form);
    const submitBtn = form.querySelector('[type="submit"]');
    const originalText = submitBtn?.textContent;

    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = window.cartTranslations?.adding || 'Adding...';
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        await this.refreshCart();
        this.open();
      } else {
        const error = await response.json();
        const msg = error.description || window.cartTranslations?.addError || 'Could not add item to cart';
        this.showCartAlert(msg);
      }
    } catch (error) {
      console.error('Add to cart error:', error);
      this.showCartAlert(window.cartTranslations?.addError || 'Could not add item to cart');
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = originalText;
      }
    }
  }

  async handleQuantityChange(btn) {
    const key = btn.dataset.key;
    const action = btn.dataset.action;
    const item = btn.closest('[data-cart-item]');
    const qtyValue = item.querySelector('[data-qty-value]');
    let quantity = parseInt(qtyValue.textContent);

    if (action === 'increase') {
      quantity += 1;
    } else if (action === 'decrease') {
      quantity = Math.max(0, quantity - 1);
    }

    await this.updateItem(key, quantity);
  }

  async handleRemoveItem(btn) {
    const key = btn.dataset.key;
    await this.updateItem(key, 0);
  }

  async updateItem(key, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id: key, quantity })
      });

      if (response.ok) {
        await this.refreshCart();
      } else {
        this.showCartAlert(window.cartTranslations?.updateError || 'Could not update cart');
      }
    } catch (error) {
      console.error('Update cart error:', error);
      this.showCartAlert(window.cartTranslations?.updateError || 'Could not update cart');
    }
  }

  async refreshCart() {
    try {
      // Fetch updated cart drawer HTML
      const response = await fetch('/?section_id=cart-drawer');
      const html = await response.text();

      const temp = document.createElement('div');
      temp.innerHTML = html;

      const newContent = temp.querySelector('[data-cart-drawer-content]');
      const newFooter = temp.querySelector('.cart-drawer__footer');

      if (this.content && newContent) {
        this.content.innerHTML = newContent.innerHTML;
      }

      const currentFooter = this.drawer.querySelector('.cart-drawer__footer');
      if (currentFooter && newFooter) {
        currentFooter.innerHTML = newFooter.innerHTML;
      } else if (newFooter && !currentFooter) {
        this.drawer.querySelector('.cart-drawer__panel').appendChild(newFooter);
      } else if (!newFooter && currentFooter) {
        currentFooter.remove();
      }

      // Update cart count in header
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      this.updateCartCount(cart.item_count);

    } catch (error) {
      console.error('Refresh cart error:', error);
    }
  }

  showCartAlert(message) {
    // Remove any existing alert
    const existing = this.drawer.querySelector('.cart-drawer__alert');
    if (existing) existing.remove();

    const alert = document.createElement('div');
    alert.className = 'cart-drawer__alert';
    alert.textContent = message;
    this.content.insertBefore(alert, this.content.firstChild);

    setTimeout(() => alert.remove(), 4000);
  }

  updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });
  }
}

// Initialize
document.addEventListener('DOMContentLoaded', () => {
  new CartDrawer();
});
